import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import type { Invoice, InvoiceItem } from '../types/invoice';
import type { User } from '../types';
import type { B2BBuyer } from '../types/b2b';

interface ReservationForPdf {
  reservation_number: string;
  created_at: string;
  total_amount: number;
  pickup_scheduled_date?: string;
}

interface ReservationItemForPdf {
  name: string;
  sku?: string;
  color_name?: string;
  size_name?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export interface SupplierProfile {
  company_name?: string;
  legal_entity_name?: string;
  company_street?: string;
  company_house_number?: string;
  company_postal_code?: string;
  company_city?: string;
  company_country?: string;
  company_phone?: string;
  company_email?: string;
  company_website?: string;
  company_address?: string;
  kvk_number?: string;
  vat_number?: string;
  company_tax_id?: string;
  iban?: string;
  bic?: string;
  bank_name?: string;
  logo_url?: string;
  payment_terms_days?: number;
}

function fmtCurrency(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('en-EU', { style: 'currency', currency }).format(amount);
}

function fmtDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function buildAddress(s: SupplierProfile): string {
  if (s.company_street) {
    const parts = [
      `${s.company_street}${s.company_house_number ? ' ' + s.company_house_number : ''}`,
      `${s.company_postal_code || ''} ${s.company_city || ''}`.trim(),
      s.company_country && s.company_country !== 'NL' ? s.company_country : null,
    ].filter(Boolean);
    return parts.join('<br>');
  }
  return s.company_address?.replace(/\n/g, '<br>') || '';
}

function buildLogoBlock(s: SupplierProfile): string {
  const name = s.company_name || '';
  if (s.logo_url) {
    return `<img src="${s.logo_url}" alt="${name}" style="max-width:120px;max-height:100px;object-fit:contain;display:block;margin-bottom:10px;" />`;
  }
  return `<div style="font-size:24px;font-weight:800;color:#1a1a2e;letter-spacing:-0.5px;margin-bottom:10px;">${name}</div>`;
}

function buildSupplierBlock(s: SupplierProfile): string {
  const lines: string[] = [];
  lines.push(buildLogoBlock(s));
  if (s.company_name) lines.push(`<div style="font-size:16px;font-weight:700;color:#1a1a2e;">${s.company_name}</div>`);
  const addr = buildAddress(s);
  if (addr) lines.push(`<div style="font-size:12px;color:#555;line-height:1.6;margin-top:4px;">${addr}</div>`);

  const legal: string[] = [];
  if (s.legal_entity_name) legal.push(s.legal_entity_name);
  if (s.kvk_number) legal.push(`KvK: ${s.kvk_number}`);
  if (s.vat_number || s.company_tax_id) legal.push(`VAT: ${s.vat_number || s.company_tax_id}`);
  if (legal.length) lines.push(`<div style="font-size:11px;color:#666;margin-top:8px;">${legal.join('<br>')}</div>`);

  const banking: string[] = [];
  if (s.iban) banking.push(`IBAN: ${s.iban}`);
  if (s.bic) banking.push(`BIC: ${s.bic}`);
  if (s.bank_name) banking.push(s.bank_name);
  if (banking.length) lines.push(`<div style="font-size:11px;color:#666;margin-top:8px;">${banking.join('<br>')}</div>`);

  const contact: string[] = [];
  if (s.company_website) contact.push(s.company_website);
  if (s.company_email) contact.push(s.company_email);
  if (s.company_phone) contact.push(`Tel: ${s.company_phone}`);
  if (contact.length) lines.push(`<div style="font-size:11px;color:#666;margin-top:8px;">${contact.join('<br>')}</div>`);

  return lines.join('\n');
}

export async function buildEpcQrDataUrl(
  supplier: SupplierProfile,
  amount: number,
  reference: string
): Promise<string | null> {
  if (!supplier.iban || !supplier.bic) return null;
  const beneficiary = (supplier.company_name || '').substring(0, 70);
  const payload = [
    'BCD', '002', '1', 'SCT',
    supplier.bic,
    beneficiary,
    supplier.iban,
    `EUR${amount.toFixed(2)}`,
    '', '', reference,
  ].join('\n');

  try {
    return await QRCode.toDataURL(payload, { width: 240, margin: 1, errorCorrectionLevel: 'M' });
  } catch {
    return null;
  }
}

function fmtNlDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('nl-NL', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function fmtNlNumber(amount: number): string {
  // Returns "8.312,73" style (Dutch number formatting, no currency symbol)
  return new Intl.NumberFormat('nl-NL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function fmtNlCurrency(amount: number): string {
  // Returns "€ 8.312,73" style (currency symbol + Dutch number formatting)
  return `&euro; ${fmtNlNumber(amount)}`;
}

interface KvRow {
  label: string;
  value: string;
}

function buildSupplierKv(s: SupplierProfile): KvRow[] {
  const kv: KvRow[] = [];
  if (s.legal_entity_name) kv.push({ label: 'Bedrijfsnaam', value: s.legal_entity_name });
  if (s.kvk_number) kv.push({ label: 'Kvk nummer', value: s.kvk_number });
  if (s.vat_number || s.company_tax_id) kv.push({ label: 'BTW-nummer', value: s.vat_number || s.company_tax_id || '' });
  if (s.iban) kv.push({ label: 'IBAN', value: s.iban });
  if (s.bic) kv.push({ label: 'BIC', value: s.bic });
  if (s.company_website) kv.push({ label: 'Website', value: s.company_website });
  if (s.company_email) kv.push({ label: 'Email', value: s.company_email });
  return kv;
}

function defaultCustomerKv(invoice: Invoice): KvRow[] {
  const kv: KvRow[] = [];
  if (invoice.customer_name) kv.push({ label: 'Bedrijfsnaam', value: invoice.customer_name });
  if (invoice.customer_address) {
    const lines = invoice.customer_address.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines[0]) kv.push({ label: 'Adres', value: lines[0] });
    if (lines[1]) kv.push({ label: 'Postcode', value: lines[1] });
  }
  if (invoice.customer_vat_number) kv.push({ label: 'BTW-nummer', value: invoice.customer_vat_number });
  if (invoice.customer_email) kv.push({ label: 'Email', value: invoice.customer_email });
  return kv;
}

function renderKvTable(rows: KvRow[], cls = 'kv'): string {
  if (rows.length === 0) return '';
  return `<table class="${cls}">${rows.map(r => `
    <tr><td class="kv-label">${r.label}</td><td class="kv-value">${r.value}</td></tr>
  `).join('')}</table>`;
}

function invoiceCSS(): string {
  return `
    @page { margin: 18mm 14mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 12px; color: #1a1a2e; line-height: 1.5; padding: 32px; background: #fff; }
    .inv { max-width: 780px; margin: 0 auto; }

    /* Top header: company info (left), logo (right) */
    .inv-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; margin-bottom: 24px; }
    .inv-top-left { flex: 1; min-width: 0; }
    .inv-top-right { flex-shrink: 0; max-width: 220px; text-align: right; }
    .inv-top-right img { max-width: 220px; max-height: 110px; object-fit: contain; }

    .company-name { font-size: 18px; font-weight: 800; color: #1a1a2e; margin-bottom: 4px; }
    .company-addr { font-size: 12px; color: #1a1a2e; line-height: 1.55; margin-bottom: 14px; }

    /* Key/value tables */
    table.kv, table.kv-cust, table.kv-meta { border-collapse: collapse; }
    table.kv td, table.kv-cust td, table.kv-meta td { padding: 2px 0; vertical-align: top; font-size: 12px; }
    .kv-label { color: #6b6b7a; padding-right: 24px !important; white-space: nowrap; }
    .kv-value { color: #1a1a2e; font-weight: 500; }

    /* Title */
    .inv-title { font-size: 30px; font-weight: 800; color: #1a1a2e; letter-spacing: -0.5px; margin: 28px 0 16px; }

    /* Customer + meta row */
    .inv-cust-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 32px; margin-bottom: 28px; }
    table.kv-cust td { padding: 3px 0; }
    table.kv-cust .kv-label { padding-right: 18px !important; }
    table.kv-meta td { padding: 3px 0; }
    table.kv-meta .kv-label { padding-right: 14px !important; }

    /* Items table */
    table.items { width: 100%; border-collapse: collapse; margin-top: 8px; }
    table.items thead th { border-top: 1.5px solid #1a1a2e; border-bottom: 1.5px solid #1a1a2e; font-size: 11px; font-weight: 700; padding: 8px 6px; text-align: left; color: #1a1a2e; }
    table.items thead th.qty { width: 12%; }
    table.items thead th.desc { width: 50%; }
    table.items thead th.price { width: 18%; text-align: right; }
    table.items thead th.amount { width: 20%; text-align: right; }
    table.items tbody td { padding: 8px 6px; font-size: 12px; vertical-align: top; color: #1a1a2e; }
    table.items tbody td.qty { text-align: left; font-weight: 600; }
    table.items tbody td.price, table.items tbody td.amount { text-align: right; font-variant-numeric: tabular-nums; }
    table.items tbody td.amount { font-weight: 600; }
    table.items tbody td .prod { font-weight: 600; color: #1a1a2e; }
    table.items tbody td .variant { font-size: 11px; color: #6b6b7a; margin-top: 2px; }
    table.items tbody td .muted { color: #888; font-weight: 400; }

    /* Totals box */
    .totals-row { display: flex; justify-content: flex-end; margin-top: 12px; }
    table.totals-box { border-collapse: collapse; min-width: 280px; }
    table.totals-box td { padding: 5px 8px; font-size: 12px; }
    table.totals-box td.label { color: #1a1a2e; }
    table.totals-box td.cur { text-align: left; padding-left: 20px; }
    table.totals-box td.num { text-align: right; font-variant-numeric: tabular-nums; min-width: 90px; }
    table.totals-box tr.grand td { border-top: 1.5px solid #1a1a2e; border-bottom: 1.5px solid #1a1a2e; font-weight: 800; padding-top: 7px; padding-bottom: 7px; }

    /* Payment block + QR */
    .inv-pay-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; margin-top: 32px; }
    .inv-pay-text { flex: 1; font-size: 12px; color: #1a1a2e; line-height: 1.6; }
    .inv-pay-text p { margin-bottom: 4px; }
    .inv-pay-qr { flex-shrink: 0; text-align: center; }
    .inv-pay-qr img { display: block; margin: 0 auto 4px; }
    .inv-pay-qr-label { font-size: 10px; color: #6b6b7a; line-height: 1.3; }

    /* Footer */
    .inv-footer-note { font-size: 10.5px; color: #6b6b7a; line-height: 1.5; margin-top: 36px; padding-top: 14px; border-top: 1px solid #e0e0e0; }

    /* ---- Legacy classes (used by reservation PDF) ---- */
    .inv-header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 24px; border-bottom: 3px solid #1a1a2e; }
    .inv-header-left { max-width: 55%; }
    .inv-header-right { text-align: right; }
    .inv-number { font-size: 14px; color: #555; }
    .inv-meta { display: flex; justify-content: space-between; margin-top: 24px; margin-bottom: 24px; }
    .inv-meta-box { flex: 1; }
    .inv-meta-box.right { text-align: right; }
    .inv-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #888; margin-bottom: 6px; }
    .inv-value { font-size: 13px; color: #333; }
    .inv-value strong { font-weight: 700; color: #1a1a2e; }
    table.legacy, .inv-meta + table { width: 100%; border-collapse: collapse; margin: 20px 0 0; }
    .inv-meta + table thead th { background: #1a1a2e; color: #fff; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 10px 14px; text-align: left; }
    .inv-meta + table thead th.num { text-align: right; }
    .inv-meta + table thead th.center { text-align: center; }
    .inv-meta + table tbody td { padding: 10px 14px; border-bottom: 1px solid #e8e8e8; font-size: 13px; }
    .inv-meta + table tbody tr:nth-child(even) td { background: #f9fafb; }
    .inv-meta + table tbody td.num { text-align: right; font-variant-numeric: tabular-nums; }
    .inv-meta + table tbody td.center { text-align: center; }
    .totals td { border-bottom: none; padding: 8px 14px; }
    .totals .grand-total td { border-top: 2px solid #1a1a2e; font-size: 16px; font-weight: 800; color: #1a1a2e; padding-top: 12px; padding-bottom: 12px; }
    .inv-footer { display: grid; grid-template-columns: 160px 1fr; align-items: end; gap: 24px; margin-top: 32px; padding-top: 20px; border-top: 1px solid #e0e0e0; }
    .inv-footer-nqr { grid-column: 1 / -1; text-align: right; }
    .inv-thanks { font-size: 15px; font-weight: 600; color: #1a1a2e; margin-bottom: 4px; }
  `;
}

export function generateInvoiceHTMLNew(
  invoice: Invoice,
  supplier: SupplierProfile,
  opts?: {
    titleOverride?: string;
    epcQrDataUrl?: string | null;
    customerKv?: KvRow[];
  }
): string {
  const title = opts?.titleOverride || 'INVOICE';
  const vatPct = invoice.subtotal_amount > 0
    ? Math.round((invoice.tax_amount / invoice.subtotal_amount) * 100)
    : 21;
  const paymentTerms = supplier.payment_terms_days || 14;
  const qr = opts?.epcQrDataUrl ?? null;

  const supplierTopLeft = (() => {
    const parts: string[] = [];
    if (supplier.company_name) {
      parts.push(`<div class="company-name">${supplier.company_name}</div>`);
    }
    const addrLines: string[] = [];
    if (supplier.company_street) {
      addrLines.push(`${supplier.company_street}${supplier.company_house_number ? ' ' + supplier.company_house_number : ''}`);
    }
    const postalCity = `${supplier.company_postal_code || ''} ${supplier.company_city || ''}`.trim();
    if (postalCity) addrLines.push(postalCity);
    if (supplier.company_country) addrLines.push(supplier.company_country);
    if (addrLines.length === 0 && supplier.company_address) {
      addrLines.push(...supplier.company_address.split('\n'));
    }
    if (addrLines.length > 0) {
      parts.push(`<div class="company-addr">${addrLines.join('<br>')}</div>`);
    }
    parts.push(renderKvTable(buildSupplierKv(supplier), 'kv'));
    return parts.join('\n');
  })();

  const supplierTopRight = supplier.logo_url
    ? `<img src="${supplier.logo_url}" alt="${supplier.company_name || ''}" />`
    : '';

  const customerKv = opts?.customerKv && opts.customerKv.length > 0
    ? opts.customerKv
    : defaultCustomerKv(invoice);

  const metaKv: KvRow[] = [
    { label: 'Factuurnummer', value: invoice.invoice_number },
    { label: 'Factuurdatum', value: fmtNlDate(invoice.invoice_date) },
  ];

  const itemRows = (invoice.invoice_items || []).map(item => {
    const it = item as InvoiceItem & {
      product_name?: string | null;
      sku?: string | null;
      color_name?: string | null;
      size_name?: string | null;
    };
    const productLabel = (it.product_name && it.product_name.trim())
      || (it.description ? it.description.split(' — ')[0] : '')
      || 'Item';
    const skuTxt = it.sku ? ` <span class="muted">(${it.sku})</span>` : '';
    const variant = [it.color_name, it.size_name].filter(Boolean).join(' / ');
    const variantLine = variant
      ? `<div class="variant">${variant}</div>`
      : (it.description && it.description.includes(' — ')
          ? `<div class="variant">${it.description.split(' — ').slice(1).join(' — ')}</div>`
          : '');
    const qty = (it.quantity ?? '').toString();
    return `
    <tr>
      <td class="qty">${qty}</td>
      <td class="desc">
        <div class="prod">${productLabel}${skuTxt}</div>
        ${variantLine}
      </td>
      <td class="price">${fmtNlCurrency(it.unit_price)}</td>
      <td class="amount">${fmtNlCurrency(it.total_price)}</td>
    </tr>`;
  }).join('');

  const beneficiary = supplier.legal_entity_name || supplier.company_name || '';
  const paymentBlock = `
    <div class="inv-pay-text">
      <p>Te betalen binnen ${paymentTerms} dagen op rekening:</p>
      ${supplier.iban ? `<p><strong>${supplier.iban}</strong>${beneficiary ? ' t.n.v. ' + beneficiary : ''} onder vermelding van klantnummer en factuurnummer.</p>` : ''}
      ${supplier.bic && !supplier.iban ? `<p>BIC: ${supplier.bic}</p>` : ''}
    </div>`;

  const qrBlock = qr
    ? `<div class="inv-pay-qr">
        <img src="${qr}" width="110" height="110" alt="Scan to pay" />
        <div class="inv-pay-qr-label">Scan om te betalen</div>
      </div>`
    : '';

  const footerNote = (supplier as any).invoice_footer_note as string | undefined;

  return `<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="UTF-8">
  <title>${title} ${invoice.invoice_number}</title>
  <style>${invoiceCSS()}</style>
</head>
<body>
  <div class="inv">
    <div class="inv-top">
      <div class="inv-top-left">${supplierTopLeft}</div>
      <div class="inv-top-right">${supplierTopRight}</div>
    </div>

    <div class="inv-title">${title}</div>

    <div class="inv-cust-row">
      ${renderKvTable(customerKv, 'kv-cust')}
      ${renderKvTable(metaKv, 'kv-meta')}
    </div>

    <table class="items">
      <thead>
        <tr>
          <th class="qty">Aantal</th>
          <th class="desc">Omschrijving</th>
          <th class="price">Prijs per stuk</th>
          <th class="amount">Bedrag</th>
        </tr>
      </thead>
      <tbody>${itemRows}</tbody>
    </table>

    <div class="totals-row">
      <table class="totals-box">
        <tr>
          <td class="label">Totaal excl. BTW</td>
          <td class="cur">&euro;</td>
          <td class="num">${fmtNlNumber(invoice.subtotal_amount)}</td>
        </tr>
        <tr>
          <td class="label">${vatPct}% BTW</td>
          <td class="cur">&euro;</td>
          <td class="num">${fmtNlNumber(invoice.tax_amount)}</td>
        </tr>
        <tr class="grand">
          <td class="label">Totaal incl. BTW</td>
          <td class="cur">&euro;</td>
          <td class="num">${fmtNlNumber(invoice.total_amount)}</td>
        </tr>
      </table>
    </div>

    <div class="inv-pay-row">
      ${paymentBlock}
      ${qrBlock}
    </div>

    ${footerNote ? `<div class="inv-footer-note">${footerNote.replace(/\n/g, '<br>')}</div>` : ''}
  </div>
</body>
</html>`;
}

export function generateReservationHTML(
  reservation: ReservationForPdf,
  buyer: B2BBuyer,
  items: ReservationItemForPdf[],
  supplier: SupplierProfile
): string {
  const currency = buyer.preferred_currency || 'EUR';
  const pickupDate = reservation.pickup_scheduled_date
    ? fmtDate(reservation.pickup_scheduled_date)
    : 'To be confirmed';

  const itemRows = items.map(item => {
    const variant = [item.color_name, item.size_name].filter(Boolean).join(' / ');
    return `
    <tr>
      <td>
        ${item.name}${item.sku ? ` <span style="color:#888;">(${item.sku})</span>` : ''}
        ${variant ? `<br><span style="font-size:11px;color:#555;">${variant}</span>` : ''}
      </td>
      <td class="center">${item.quantity}</td>
      <td class="num">${fmtCurrency(item.unit_price, currency)}</td>
      <td class="num">${fmtCurrency(item.total_price, currency)}</td>
    </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Reservation ${reservation.reservation_number}</title>
  <style>${invoiceCSS()}</style>
</head>
<body>
  <div class="inv">
    <div class="inv-header">
      <div class="inv-header-left">
        ${buildSupplierBlock(supplier)}
      </div>
      <div class="inv-header-right">
        <div class="inv-title">RESERVATION</div>
        <div class="inv-number">${reservation.reservation_number}</div>
      </div>
    </div>

    <div class="inv-meta">
      <div class="inv-meta-box">
        <div class="inv-label">Reserved By</div>
        <div class="inv-value"><strong>${buyer.trade_name}</strong></div>
        <div class="inv-value">${buyer.contact_person_name}</div>
        ${buyer.billing_address ? `<div class="inv-value">${buyer.billing_address}</div>` : ''}
        ${buyer.billing_postal_code || buyer.billing_city ? `<div class="inv-value">${(buyer.billing_postal_code || '')} ${(buyer.billing_city || '')}`.trim() + `</div>` : ''}
        ${buyer.vat_number ? `<div class="inv-value">VAT: ${buyer.vat_number}</div>` : ''}
      </div>
      <div class="inv-meta-box right">
        <div style="margin-bottom:12px;">
          <div class="inv-label">Reservation Date</div>
          <div class="inv-value">${fmtDate(reservation.created_at)}</div>
        </div>
        <div>
          <div class="inv-label">Scheduled Pickup</div>
          <div class="inv-value" style="font-weight:700;color:#1e40af;">${pickupDate}</div>
        </div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th style="width:50%;">Item</th>
          <th class="center" style="width:12%;">Qty</th>
          <th class="num" style="width:19%;">Unit Price</th>
          <th class="num" style="width:19%;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows}
      </tbody>
      <tbody class="totals">
        <tr class="grand-total">
          <td colspan="3" style="text-align:right;">Total</td>
          <td class="num">${fmtCurrency(reservation.total_amount, currency)}</td>
        </tr>
      </tbody>
    </table>

    <div class="inv-footer">
      <div class="inv-footer-nqr">
        <div class="inv-thanks">Thank you for your reservation!</div>
        <div class="inv-footer-note">${supplier.company_name || ''}${supplier.vat_number || supplier.company_tax_id ? ' &middot; ' + (supplier.vat_number || supplier.company_tax_id) : ''}</div>
      </div>
    </div>
  </div>
</body>
</html>`;
}

export function generateProFormaInvoiceHTML(
  invoice: Invoice,
  buyer: B2BBuyer,
  supplier: SupplierProfile,
  epcQrDataUrl?: string | null
): string {
  const customerAddress = [
    buyer.billing_address,
    `${buyer.billing_postal_code || ''} ${buyer.billing_city || ''}`.trim(),
    buyer.billing_country && buyer.billing_country !== 'NL' ? buyer.billing_country : null,
  ].filter(Boolean).join('\n');

  const filledInvoice: Invoice = {
    ...invoice,
    customer_name: buyer.trade_name,
    customer_address: customerAddress,
    customer_vat_number: buyer.vat_number,
  };

  const postcodeLine = `${buyer.billing_postal_code || ''} ${buyer.billing_city || ''}`.trim();
  const customerKv: KvRow[] = [];
  if (buyer.trade_name) customerKv.push({ label: 'Bedrijfsnaam', value: buyer.trade_name });
  if (buyer.billing_address) customerKv.push({ label: 'Adres', value: buyer.billing_address });
  if (postcodeLine) customerKv.push({ label: 'Postcode', value: postcodeLine });
  if ((buyer as any).company_registration_number) {
    customerKv.push({ label: 'KvK nummer', value: (buyer as any).company_registration_number });
  }
  if (buyer.vat_number) customerKv.push({ label: 'BTW-nummer', value: buyer.vat_number });
  if (buyer.email) customerKv.push({ label: 'Email', value: buyer.email });

  return generateInvoiceHTMLNew(filledInvoice, supplier, {
    titleOverride: 'INVOICE',
    epcQrDataUrl: epcQrDataUrl ?? null,
    customerKv,
  });
}

export async function htmlToPdfBase64(
  html: string,
  opts?: { scale?: number; jpegQuality?: number }
): Promise<string> {
  const scale = opts?.scale ?? 2;
  const jpegQuality = opts?.jpegQuality ?? 0.95;

  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '794px'; // A4 at 96dpi
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 794,
    });

    const imgData = canvas.toDataURL('image/jpeg', jpegQuality);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = 210;
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    let yOffset = 0;
    const pageHeight = 297;

    while (yOffset < pdfHeight) {
      if (yOffset > 0) pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, -yOffset, pdfWidth, pdfHeight);
      yOffset += pageHeight;
    }

    const raw = pdf.output('datauristring');
    return raw.split(',')[1];
  } finally {
    document.body.removeChild(container);
  }
}
